import React, { MouseEventHandler, ReactElement, Ref } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classnames from "classnames";
import { IconDefinition } from "@fortawesome/fontawesome-common-types";

export type StyleProps = {
  icon?: string
  passwordIcon?:string
  label?:string
  input?:string
  error?:string
  resetPassword?:string
  errorMessage?:string
  container?:string
}

export type Props<N extends string=string> = {
  type: string
  title?: string
  inputName: N
  inputValue?: string
  checked?: boolean
  styles?: StyleProps
  changeCallback?: (inputName:N, inputValue:string)=>void
  focusOutCallback?: Function
  errorState?: string
  errorMessage?:string
  placeholder?:string
  icon?:IconDefinition
  iconClickHandler?:MouseEventHandler
  resetPassword?: boolean
  svg?:string
  disabled?:boolean
  checkChangedCallback?: (inputName:string|undefined, checked:boolean)=>void
  keyDown?:(inputName:string|undefined, inputValue:string)=>void
  autoFocus?: boolean
  maxLength?: number
}

type ElementType = HTMLInputElement
type RefType = Ref<HTMLInputElement>

const GenericInput = React.forwardRef<ElementType,Props>((props:Props, ref:RefType):ReactElement => {
  const {
    type,
    title,
    inputName,
    inputValue,
    checked,
    changeCallback,
    focusOutCallback,
    errorState,
    errorMessage,
    placeholder,
    icon,
    iconClickHandler,
    styles,
    svg,
    disabled,
    checkChangedCallback,
    resetPassword,
    keyDown,
    autoFocus,
    maxLength
  } = props;

  function handleOnChange(event: any) {
    if (changeCallback) {
      const { value } = event.target;
      changeCallback(inputName, value);
    }
    if (checkChangedCallback) {
      const { checked } = event.target
      checkChangedCallback(inputName, checked)
    }
  }

  function handleOnBlur() {
    if (focusOutCallback && inputValue === "") {
      focusOutCallback(inputName, inputValue);
    }
  }
  
  function handleKeyDown(event: any) {
    if (event.key === 'Enter' && keyDown !== undefined) {
      const { value } = event.target;
      keyDown(inputName, value)
    }
  }

  function renderInputIcon() {
    if (icon) {
      if (iconClickHandler) {
        return (
          <FontAwesomeIcon
            className={styles?.passwordIcon}
            icon={icon as any}
            onClick={iconClickHandler}
          />
        );
      } else {
        return <FontAwesomeIcon className={styles?.icon} icon={icon as any} />;
      }
    } else if (svg) {
      if (iconClickHandler) {
        return (
          <img
            alt={svg}
            className={styles?.passwordIcon}
            src={svg}
            onClick={iconClickHandler}
          ></img>
        );
      } else {
        return <img alt={icon} className={styles?.icon} src={svg}></img>;
      }
    }
  }

  function renderTitle() {
    if (title) {
      return <label className={styles?.label}>{title}</label>;
    }
  }

  const inputClassNames = {
    [`${styles?.input}`]: true,
    [`${styles?.error}`]: errorState,
    [`${styles?.resetPassword}`]: resetPassword
  }
  const inputClass = classnames(inputClassNames);

  return (
    <div style={{ position: "relative" }} className={styles?.container}>
      {renderTitle()}
      {renderInputIcon()}
      <input
        className={inputClass}
        type={type}
        name={inputName}
        value={inputValue}
        checked={checked}
        onBlur={handleOnBlur}
        onChange={handleOnChange}
        placeholder={placeholder ? placeholder : ""}
        disabled={disabled}
        ref={ref}
        onKeyDown={handleKeyDown}
        autoFocus={autoFocus ? true: false}
        maxLength={maxLength || 255}
      />
      <div className={styles?.errorMessage}>{errorMessage}</div>
    </div>
  );
});

export default GenericInput;
